import { ReactElement, useCallback, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Announcement,
  AnnouncementType,
  CreateAnnouncementData,
  Language,
  UpdateAnnouncementData,
} from '@breathelife/types';
import { Box, Grid } from '@breathelife/mui';
import { Input as TextInput } from '@breathelife/ui-components';

import { Drawer } from '../../../Components/Drawer/Drawer';
import { Select } from '../../../Components/Select/Select';
import { CarrierContext } from '../../../Context/CarrierContext';
import { getLanguage } from '../../../Localization/utils';
import { AnnouncementDrawerHeader } from './AnnouncementDrawerHeader';

export type AnnouncementFormState = {
  title: Partial<Record<Language, string>>;
  text: Partial<Record<Language, string>>;
  type: AnnouncementType;
  published: boolean;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateAnnouncementData | UpdateAnnouncementData) => void;
  onDelete?: () => void;
  announcement?: Announcement;
  isLoading?: boolean;
};

const getDefaultFormState = (enabledLanguages: Language[], announcement?: Announcement): AnnouncementFormState => {
  const title: Partial<Record<Language, string>> = {};
  const text: Partial<Record<Language, string>> = {};

  enabledLanguages.forEach((lang) => {
    title[lang] = announcement?.title?.[lang] || '';
    text[lang] = announcement?.text?.[lang] || '';
  });

  return {
    title,
    text,
    type: announcement?.type || AnnouncementType.info,
    published: announcement?.published ?? true,
  };
};

export function AnnouncementEditorDrawer(props: Props): ReactElement {
  const { isOpen, onClose, onSave, onDelete, announcement, isLoading } = props;
  const { t } = useTranslation();
  const { languageSettings } = useContext(CarrierContext);
  const enabledLanguages = languageSettings.enabledLanguages;

  const [formState, setFormState] = useState<AnnouncementFormState>(
    getDefaultFormState(enabledLanguages, announcement),
  );

  useEffect(() => {
    if (isOpen) {
      setFormState(getDefaultFormState(enabledLanguages, announcement));
    }
  }, [isOpen, enabledLanguages, announcement]);

  const handleTitleChange = useCallback((language: Language, value: string) => {
    setFormState((prev) => ({
      ...prev,
      title: { ...prev.title, [language]: value },
    }));
  }, []);

  const handleTextChange = useCallback((language: Language, value: string) => {
    setFormState((prev) => ({
      ...prev,
      text: { ...prev.text, [language]: value },
    }));
  }, []);

  const handleTypeChange = useCallback((value: AnnouncementType) => {
    setFormState((prev) => ({
      ...prev,
      type: value,
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormState(getDefaultFormState(enabledLanguages));
  }, [enabledLanguages]);

  const onCloseDrawer = useCallback(() => {
    onClose();
    resetForm();
  }, [onClose, resetForm]);

  const handleSave = useCallback(() => {
    const data: CreateAnnouncementData | UpdateAnnouncementData = {
      title: formState.title as Record<Language, string>,
      text: formState.text as Record<Language, string>,
      type: formState.type,
      published: formState.published,
    };

    onSave(data);
  }, [formState, onSave]);

  const isFormValid = enabledLanguages.every((lang) => formState.title[lang]?.trim() && formState.text[lang]?.trim());

  const isEditMode = Boolean(announcement);
  const headerTitle = isEditMode ? announcement?.title?.[getLanguage()] || announcement?.title?.en || '' : '';

  const typeOptions = [
    { value: AnnouncementType.info, label: t('admin.announcementsManagement.type.info') },
    { value: AnnouncementType.warning, label: t('admin.announcementsManagement.type.warning') },
    { value: AnnouncementType.success, label: t('admin.announcementsManagement.type.success') },
    { value: AnnouncementType.error, label: t('admin.announcementsManagement.type.error') },
  ];

  return (
    <Drawer open={isOpen} onClose={onCloseDrawer}>
      <AnnouncementDrawerHeader
        onClose={onCloseDrawer}
        onSave={handleSave}
        onDelete={isEditMode ? onDelete : undefined}
        isFormValid={isFormValid}
        isLoading={isLoading}
        title={headerTitle}
        isEditMode={isEditMode}
      />
      <Box p={3}>
        <Grid container spacing={2}>
          {enabledLanguages.map((language) => (
            <Grid item xs={12} key={`announcement-title-${language}`}>
              <TextInput
                name={`announcement-title-${language}`}
                inputVariant='outlined'
                label={t('admin.announcementsManagement.form.titleLabel', {
                  language: t(`language.${language}`),
                })}
                value={formState.title[language] || ''}
                required
                onChange={(event) => handleTitleChange(language, event.target.value)}
              />
            </Grid>
          ))}

          {enabledLanguages.map((language) => (
            <Grid item xs={12} key={`announcement-text-${language}`}>
              <TextInput
                name={`announcement-text-${language}`}
                inputVariant='outlined'
                label={t('admin.announcementsManagement.form.textLabel', {
                  language: t(`language.${language}`),
                })}
                value={formState.text[language] || ''}
                required
                multiline
                rows={4}
                onChange={(event) => handleTextChange(language, event.target.value)}
              />
            </Grid>
          ))}

          <Grid item xs={12}>
            <Select
              id='announcement-type'
              label={t('admin.announcementsManagement.form.typeLabel')}
              value={formState.type}
              options={typeOptions}
              onChange={handleTypeChange}
              data-testid='announcement-type-select'
            />
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  );
}
